import React from 'react';
import { Card, CardContent, Badge } from '@roger/ui-components';
import { Calendar, User, Stethoscope } from 'lucide-react';
import {
  QAVisitWithNonStandardDateKey,
  QADataItem,
} from '../types/visitData.types';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';
import {
  getQAStatusDisplayLabel,
  getQAStatusBadgeStyles,
} from '../utils/qaStatusUtils';

const getLatestQAStatus = (qaData?: QADataItem[]): string | null => {
  if (!qaData || qaData.length === 0) return null;
  const sorted = [...qaData].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  return sorted[0].status;
};

interface QAVisitCardProps {
  visit: QAVisitWithNonStandardDateKey;
  onClick: (visit: QAVisitWithNonStandardDateKey) => void;
}

const QAVisitCard: React.FC<QAVisitCardProps> = ({ visit, onClick }) => {
  const latestStatus = getLatestQAStatus(visit.qa_data);

  return (
    <Card
      className='transition-all duration-200 cursor-pointer hover:bg-blue-50 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 border-gray-200'
      onClick={() => onClick(visit)}
    >
      <CardContent>
        <div className='flex items-center gap-4'>
          {/* Patient Name & MRN */}
          <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
            <span className='font-semibold text-gray-900 text-sm'>
              {visit.patient_first_name} {visit.patient_last_name}
            </span>
            {visit.patient_id && (
              <Badge variant='secondary' className='text-xs font-mono'>
                {visit.patient_id}
              </Badge>
            )}
          </div>

          {/* Visit Date */}
          <div className='flex items-center gap-1 w-[100px] flex-shrink-0'>
            <Calendar className='w-4 h-4 text-gray-500' />
            <span className='text-sm text-gray-600'>
              {visit.visit_date_date}
            </span>
          </div>

          {/* Nurse */}
          <div className='flex items-center gap-1 w-[140px] flex-shrink-0'>
            <User className='w-4 h-4 text-gray-500' />
            <span className='text-sm text-gray-600 truncate'>
              {visit.nurse_username}
            </span>
          </div>

          {/* Visit Type */}
          <div className='flex items-center gap-1 w-[80px] flex-shrink-0'>
            <Stethoscope className='w-4 h-4 text-gray-500' />
            <span className='text-sm text-gray-600'>
              {getVisitTypeDisplayName(visit.visit_type)}
            </span>
          </div>

          {/* QA Status */}
          <div className='flex items-center gap-1 w-[120px] flex-shrink-0 justify-end'>
            {latestStatus && (
              <Badge
                className={`text-xs ${getQAStatusBadgeStyles(latestStatus)}`}
              >
                {getQAStatusDisplayLabel(latestStatus)}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QAVisitCard;
